import {PropsWithChildren, useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {useConfig} from '@/contexts/config';
import {
  useResubmitDocumentForSignature,
  useSubmitDocumentForSignature,
  useRemoveDocumentAttachment,
  useUpdateDocument,
} from '@/hooks/queries/documents';
import {SupportedLocale} from '@/initializers/i18next';
import {FullDocument, FullDocumentReadyForSignature} from '@/interfaces/document';
import {Allocation} from '@/repositories/beneva-api/endpoints/allocations';
import {Attachment} from '@/repositories/beneva-api/endpoints/attachments';
import {DocumentRepositoryContext} from './document-repository-context';

type Props = PropsWithChildren<{
  document: FullDocument;
  allocations?: Allocation[];
  attachments?: Attachment[];
}>;

export const DocumentRepositoryProvider = ({document, allocations = [], attachments = [], children}: Props) => {
  const {t} = useTranslation();
  const {model} = useConfig();

  const {mutateAsync: resubmitForSignatureMutation, isPending: isResubmitting} = useResubmitDocumentForSignature();
  const {mutateAsync: submitForSignatureMutation, isPending: isSubmitting} = useSubmitDocumentForSignature();
  const {mutateAsync: removeDocumentAttachementMutation} = useRemoveDocumentAttachment();
  const {mutateAsync: updateDocumentMutation, isPending: isSaving, isError} = useUpdateDocument();

  const documentId = document.id;

  const saveDocument = useCallback(
    async (updatedDocument: Partial<FullDocument>) => {
      await updateDocumentMutation({documentId, updatedDocument});
    },
    [documentId, updateDocumentMutation],
  );

  const removeDocumentAttachment = useCallback(
    async (attachmentId: string) => {
      await removeDocumentAttachementMutation({documentId, attachmentId});
    },
    [documentId, removeDocumentAttachementMutation],
  );

  const submitDocumentForSignature = useCallback(
    async (fullDocument: FullDocumentReadyForSignature, locale: SupportedLocale) => {
      await submitForSignatureMutation({
        documentId,
        document: fullDocument,
        documentLocale: locale,
        modelJsonVersion: model.jsonVersion,
        signatureQuestion: t('components.document.context.signature-question', {lng: locale}),
        signatureEmailSubject: t('components.document.context.signature-email-subject', {
          lng: locale,
          firstName: fullDocument.A['A3'],
          lastName: fullDocument.A['A2'],
        }),
      });
    },
    [documentId, model.jsonVersion, submitForSignatureMutation, t],
  );

  const resubmitDocumentForSignature = useCallback(
    async (fullDocument: FullDocumentReadyForSignature, updatedDocument: FullDocument) => {
      await resubmitForSignatureMutation({
        documentId,
        serializedDocument: fullDocument,
        document: updatedDocument,
      });
    },
    [documentId, resubmitForSignatureMutation],
  );

  return (
    <DocumentRepositoryContext.Provider
      value={{
        isSaving: isSaving,
        hasSavingError: isError,
        isSubmitting: isSubmitting || isResubmitting,
        document,
        allocations,
        attachments,
        saveDocument,
        removeDocumentAttachment,
        submitDocumentForSignature,
        resubmitDocumentForSignature,
      }}
    >
      {children}
    </DocumentRepositoryContext.Provider>
  );
};
